import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import KpiStrip from './shared/KpiStrip';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, fmtMoney, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';
import { formatClinicDateTime } from '../../utils/clinicTime';
import { auditFieldLabel, formatAuditValue } from '../../utils/visitAudit';

// Corrections made to tokens after they were issued. Every row is an audit entry, so this answers
// "which token was changed, what exactly changed, and who changed it" for an admin reviewing why
// edits happen once tokens are out. Read-only: nothing here recalculates anything.
//
// A fee is Finance data: without the Finance reports permission the server keeps a fee, discount or
// payment-mode edit's row (what was changed, when, by whom) but leaves out its before and after,
// and the net fee change.

const COLUMNS = [
  {
    key: 'changedAt', label: 'When',
    render: (r) => formatClinicDateTime(r.changedAt),
    csv: (r) => r.changedAt,
  },
  { key: 'token', label: 'Token', render: (r) => <span className="font-mono">{r.token}</span> },
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN', render: (r) => <span className="font-mono text-xs">{r.mrn}</span> },
  { key: 'doctor', label: 'Doctor', render: (r) => r.doctor || '—' },
  {
    key: 'field', label: 'Changed',
    render: (r) => auditFieldLabel(r.field),
    csv: (r) => auditFieldLabel(r.field),
  },
  {
    key: 'oldValue', label: 'From',
    render: (r) => (
      <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-md font-mono text-xs">
        {formatAuditValue(r.oldValue, r.field)}
      </span>
    ),
    csv: (r) => formatAuditValue(r.oldValue, r.field),
  },
  {
    key: 'newValue', label: 'To',
    render: (r) => (
      <span className={`px-2 py-0.5 rounded-md font-mono text-xs ${
        r.action === 'TOKEN_REISSUED' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
      }`}>
        {formatAuditValue(r.newValue, r.field)}
      </span>
    ),
    csv: (r) => formatAuditValue(r.newValue, r.field),
  },
  { key: 'changedBy', label: 'Edited by', render: (r) => r.changedBy || '—' },
];

const TokenEditsReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getTokenEdits({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  // Net movement can go either way, so it is labelled by direction rather than shown as a bare
  // signed number an admin has to interpret.
  const net = Number(k.netFeeChange || 0);
  const money = data?.access?.money !== false;
  const kpis = [
    { label: 'Edits', value: k.edits ?? 0, sub: 'Separate corrections' },
    { label: 'Tokens Edited', value: k.tokensEdited ?? 0, sub: 'Distinct tokens touched' },
    { label: 'Fee Changes', value: k.feeChanges ?? 0, sub: 'Edits that moved the fee' },
    { label: 'Doctor Changes', value: k.doctorChanges ?? 0, sub: 'Reassigned to another doctor' },
    ...(money
      ? [
          {
            label: net < 0 ? 'Net Fee Reduced' : 'Net Fee Added',
            value: fmtMoney(Math.abs(net)),
            sub: 'Across all fee edits',
            // A net reduction is money the hospital stopped charging after issuing the token, which
            // is the case an admin most wants to notice.
            tone: net < 0 ? 'alert' : undefined,
          },
        ]
      : []),
  ];

  const chartRows = useMemo(
    () => (data?.series || []).map((s) => ({
      field: auditFieldLabel(s.field),
      changes: Number(s.changes),
    })),
    [data]
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <ChartPanel title="What gets corrected most often after a token is issued">
        {loading || chartRows.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No token edits in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 44)}>
            <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="field" {...axisProps} width={140} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="changes" radius={[0, 4, 4, 0]} barSize={18}>
              {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Every change made"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`token-edits-${range.from}-to-${range.to}`}
        emptyText="No token edits in the selected range"
        footnote={money ? undefined : 'The amounts in fee, discount and payment-mode changes are shown to users with the Finance reports permission.'}
      />

      <ReportPrintLayout
        title="Token Edits"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[{ title: 'Every change made', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default TokenEditsReport;
